// The lab (docs/plans/phase1-lab.md §3.3). Values, comments and clinical notes travel only
// in request bodies; addresses carry internal IDs only. A scanned sample number is looked
// up with a POST body, never a query string.
import { queryOptions } from '@tanstack/react-query';
import {
  clinicalNotificationListSchema,
  clinicalNotificationSchema,
  collectSamplesResultSchema,
  labAnalyteSchema,
  labOrderSchema,
  labReportSchema,
  labRulesSchema,
  labSampleSchema,
  labSampleWorkSchema,
  labSectionSchema,
  labSettingsSchema,
  labTestSchema,
  labValueSetConceptSchema,
  labWorklistSchema,
  patientLabResultsSchema,
  releasedLabReportSchema,
  type LabSettingsView,
  type LabValueSet,
  type LabWorklistState,
  type labAnalyteInputSchema,
  type putLabRulesSchema,
  type putLabTestDefinitionSchema,
  type saveLabResultsSchema,
  type updateLabAnalyteSchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

/** The worklist refreshes while visible, as queue screens do (plan §4.4: polls while waiting). */
export const LAB_REFRESH_MS = 10_000;
/** Plan P8: every signed-in screen polls for clinical notifications. */
export const NOTIFICATION_POLL_MS = 15_000;

export const labKeys = {
  all: ['lab'] as const,
  catalogue: ['lab', 'catalogue'] as const,
  work: ['lab', 'work'] as const,
};

// --- Catalogue --------------------------------------------------------------------------

export const labSectionsQuery = queryOptions({
  queryKey: [...labKeys.catalogue, 'sections'],
  queryFn: () => api('GET', '/lab/sections', { schema: z.array(labSectionSchema) }),
});

export const labValueSetQuery = (valueSet: LabValueSet) =>
  queryOptions({
    queryKey: [...labKeys.catalogue, 'value-sets', valueSet],
    queryFn: () =>
      api('GET', `/lab/value-sets/${valueSet}`, { schema: z.array(labValueSetConceptSchema) }),
  });

export const labAnalytesQuery = queryOptions({
  queryKey: [...labKeys.catalogue, 'analytes'],
  queryFn: () => api('GET', '/lab/analytes', { schema: z.array(labAnalyteSchema) }),
});

export const labTestsQuery = queryOptions({
  queryKey: [...labKeys.catalogue, 'tests'],
  queryFn: () => api('GET', '/lab/tests', { schema: z.array(labTestSchema) }),
});

export const labTestQuery = (serviceItemId: string) =>
  queryOptions({
    queryKey: [...labKeys.catalogue, 'tests', serviceItemId],
    queryFn: () => api('GET', `/lab/tests/${serviceItemId}`, { schema: labTestSchema }),
  });

export const labSettingsQuery = queryOptions({
  queryKey: [...labKeys.catalogue, 'settings'],
  queryFn: () => api('GET', '/lab/settings', { schema: labSettingsSchema }),
});

export const labRulesQuery = (analyteId: string) =>
  queryOptions({
    queryKey: [...labKeys.catalogue, 'rules', analyteId],
    queryFn: () => api('GET', `/lab/analytes/${analyteId}/rules`, { schema: labRulesSchema }),
  });

export function addLabSection(name: string, sortOrder: number) {
  return api('POST', '/admin/lab/sections', {
    body: { name, sortOrder },
    schema: labSectionSchema,
  });
}

export function updateLabSection(
  id: string,
  body: { name?: string; sortOrder?: number; status?: 'active' | 'inactive' },
) {
  return api('PATCH', `/admin/lab/sections/${id}`, { body, schema: labSectionSchema });
}

export function addLabValueSetConcept(valueSet: LabValueSet, display: string, sortOrder: number) {
  return api('POST', `/admin/lab/value-sets/${valueSet}`, {
    body: { display, sortOrder },
    schema: labValueSetConceptSchema,
  });
}

export function updateLabValueSetConcept(
  valueSet: LabValueSet,
  code: string,
  body: { display?: string; status?: 'active' | 'inactive' },
) {
  return api('PATCH', `/admin/lab/value-sets/${valueSet}/${encodeURIComponent(code)}`, {
    body,
    schema: labValueSetConceptSchema,
  });
}

export function addLabAnalyte(body: z.input<typeof labAnalyteInputSchema>) {
  return api('POST', '/admin/lab/analytes', { body, schema: labAnalyteSchema });
}

export function updateLabAnalyte(id: string, body: z.input<typeof updateLabAnalyteSchema>) {
  return api('PATCH', `/admin/lab/analytes/${id}`, { body, schema: labAnalyteSchema });
}

export function putLabAnalyteOptions(
  id: string,
  expectedVersion: number,
  options: { code?: string; display: string; status: 'active' | 'inactive' }[],
) {
  return api('PUT', `/admin/lab/analytes/${id}/options`, {
    body: { expectedVersion, options },
    schema: labAnalyteSchema,
  });
}

export function putLabTest(
  serviceItemId: string,
  body: z.input<typeof putLabTestDefinitionSchema>,
) {
  return api('PUT', `/admin/lab/tests/${serviceItemId}`, { body, schema: labTestSchema });
}

export function putLabSettings(body: LabSettingsView) {
  return api('PUT', '/admin/lab/settings', { body, schema: labSettingsSchema });
}

export function putLabRules(analyteId: string, body: z.input<typeof putLabRulesSchema>) {
  return api('PUT', `/admin/lab/analytes/${analyteId}/rules`, { body, schema: labRulesSchema });
}

export function approveLabRule(ruleId: string) {
  return api('POST', `/admin/lab/rules/${ruleId}/approve`, { schema: labRulesSchema });
}

// --- Worklist, samples and results ------------------------------------------------------

export const labWorklistQuery = (state: LabWorklistState) =>
  queryOptions({
    queryKey: [...labKeys.work, 'worklist', state],
    queryFn: () => api('GET', `/lab/worklist?state=${state}`, { schema: labWorklistSchema }),
    refetchInterval: LAB_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

export const visitLabOrdersQuery = (visitId: string) =>
  queryOptions({
    queryKey: [...labKeys.work, 'visit-orders', visitId],
    queryFn: () => api('GET', `/visits/${visitId}/lab-orders`, { schema: z.array(labOrderSchema) }),
  });

export const labSampleWorkQuery = (sampleId: string) =>
  queryOptions({
    queryKey: [...labKeys.work, 'samples', sampleId],
    queryFn: () => api('GET', `/lab/samples/${sampleId}`, { schema: labSampleWorkSchema }),
  });

export function findLabSample(sampleNumber: string) {
  return api('POST', '/lab/samples/find', { body: { sampleNumber }, schema: labSampleSchema });
}

export function collectLabSamples(
  visitId: string,
  body: { clientRequestId: string; orderIds: string[]; collectedAt?: string },
) {
  return api('POST', `/visits/${visitId}/lab-samples`, {
    body,
    schema: collectSamplesResultSchema,
  });
}

export function recordLabelPrinted(sampleId: string, copies: number) {
  return api('POST', `/lab-samples/${sampleId}/label-printed`, { body: { copies } });
}

export function receiveLabSample(sampleId: string) {
  return api('POST', `/lab-samples/${sampleId}/receive`, { schema: labSampleSchema });
}

export function rejectLabSample(sampleId: string, reasonCode: string, note: string | null) {
  return api('POST', `/lab-samples/${sampleId}/reject`, {
    body: { reasonCode, note },
    schema: labSampleSchema,
  });
}

export function saveLabResults(reportId: string, body: z.input<typeof saveLabResultsSchema>) {
  return api('PUT', `/lab-reports/${reportId}/results`, { body, schema: labReportSchema });
}

export function submitLabReport(
  reportId: string,
  expectedRevision: number,
  acknowledgements: { analyteId: string; reason: string }[],
  cultureCriticalReason: string | null = null,
) {
  return api('POST', `/lab-reports/${reportId}/submit`, {
    body: { expectedRevision, acknowledgements, cultureCriticalReason },
    schema: labReportSchema,
  });
}

export function validateLabReport(
  reportId: string,
  action: 'validate' | 'authorise',
  expectedRevision: number,
) {
  return api('POST', `/lab-reports/${reportId}/${action}`, {
    body: { expectedRevision },
    schema: labReportSchema,
  });
}

export function returnLabReport(reportId: string, reason: string) {
  return api('POST', `/lab-reports/${reportId}/return`, {
    body: { reason },
    schema: labReportSchema,
  });
}

export function correctLabReport(reportId: string, reason: string) {
  return api('POST', `/lab-reports/${reportId}/correct`, {
    body: { reason },
    schema: labReportSchema,
  });
}

export function markLabReportInError(reportId: string, reason: string) {
  return api('POST', `/lab-reports/${reportId}/entered-in-error`, {
    body: { reason },
    schema: labReportSchema,
  });
}

// --- Released reports and notifications -------------------------------------------------

export const releasedLabReportQuery = (reportId: string) =>
  queryOptions({
    queryKey: [...labKeys.all, 'released', reportId],
    queryFn: () =>
      api('GET', `/lab-reports/${reportId}/released`, { schema: releasedLabReportSchema }),
  });

export function recordLabReportPrinted(reportId: string, copy: number) {
  return api('POST', `/lab-reports/${reportId}/printed`, { body: { copy } });
}

export const patientLabResultsQuery = (patientId: string) =>
  queryOptions({
    queryKey: ['patients', patientId, 'lab-results'],
    queryFn: () =>
      api('GET', `/patients/${patientId}/lab-results`, { schema: patientLabResultsSchema }),
  });

export const myNotificationsQuery = queryOptions({
  queryKey: ['me', 'clinical-notifications'],
  queryFn: () =>
    api('GET', '/me/clinical-notifications', { schema: clinicalNotificationListSchema }),
  refetchInterval: NOTIFICATION_POLL_MS,
  // Critical results must reach people on every screen, even one left in the background.
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: true,
});

export const notificationReportQuery = (notificationId: string) =>
  queryOptions({
    queryKey: ['me', 'clinical-notifications', notificationId, 'report'],
    queryFn: () =>
      api('GET', `/clinical-notifications/${notificationId}/report`, {
        schema: releasedLabReportSchema,
      }),
  });

export function acknowledgeNotification(notificationId: string, note: string | null) {
  return api('POST', `/clinical-notifications/${notificationId}/acknowledge`, {
    body: { note },
    schema: clinicalNotificationSchema,
  });
}

export function recordNotificationCommunication(
  notificationId: string,
  body: { clinicianName: string; communicatedAt: string; readBack: true },
) {
  return api('POST', `/clinical-notifications/${notificationId}/record-communication`, {
    body,
    schema: clinicalNotificationSchema,
  });
}
